import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { buildHeaders } from '../utils/buildRequest';

function JsonLine({ line }) {
  const m = line.match(/^(\s*)("[\w_@. -]+")\s*:\s*(.*)/);
  if (!m) return <div style={{ color: 'var(--text-muted)' }}>{line}</div>;
  const [, indent, key, rest] = m;
  const val = rest.replace(/,\s*$/, '');
  const comma = rest.trim().endsWith(',') ? ',' : '';
  let valEl;
  if (val === 'true' || val === 'false' || val === 'null') valEl = <span style={{ color: 'var(--syn-bool)' }}>{val}</span>;
  else if (val !== '' && !isNaN(val)) valEl = <span style={{ color: 'var(--syn-number)' }}>{val}</span>;
  else if (val.startsWith('"')) valEl = <span style={{ color: 'var(--syn-string)' }}>{val}</span>;
  else valEl = <span style={{ color: 'var(--text-secondary)' }}>{val}</span>;
  return (
    <div>
      {indent}<span style={{ color: 'var(--syn-key)' }}>{key}</span>
      <span style={{ color: 'var(--text-muted)' }}>: </span>{valEl}
      <span style={{ color: 'var(--text-muted)' }}>{comma}</span>
    </div>
  );
}

function Spinner({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
      style={{ animation: 'tryit-spin 0.85s linear infinite' }}>
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
    </svg>
  );
}

function MiniField({ field, value, onChange }) {
  const isJson = field.type === 'object' || field.type === 'array';
  const style = {
    width: '100%', boxSizing: 'border-box', padding: '7px 10px',
    border: '1px solid var(--border)', borderRadius: 6,
    background: 'var(--bg-base)', color: 'var(--text-primary)',
    fontSize: 12, fontFamily: 'JetBrains Mono, monospace', outline: 'none',
  };
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
        <code style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'JetBrains Mono, monospace' }}>
          {field.field}
        </code>
        {field.required && <span style={{ fontSize: 9, color: '#ef4444', fontWeight: 700 }}>*</span>}
      </div>
      {isJson ? (
        <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={2}
          placeholder={field.example || '{}'} style={{ ...style, resize: 'vertical', lineHeight: 1.5 }} />
      ) : (
        <input type={field.type === 'number' || field.type === 'integer' ? 'number' : 'text'} value={value || ''}
          onChange={e => onChange(e.target.value)} placeholder={field.example || ''} style={style} />
      )}
    </div>
  );
}

export default function TryItPanel({ api, embedded }) {
  const { baseUrl, savedToken, setSavedToken, savedBearerKey, setSavedBearerKey, product } = useApp();
  const hasBearerKey = api.headers.some(h => h.key === 'bearerkey');
  const hasAuthToken = api.headers.some(h => h.key === 'Authorization');
  const needsCorporateAuth = api.id !== 'login' || api.body.some(f => f.field === 'token');

  const [bearerKeyValue, setBearerKeyValue] = useState(() => savedBearerKey || '');
  const [tokenValue, setTokenValue] = useState(() => savedToken || '');
  const [bodyMode, setBodyMode] = useState('json');
  const [jsonBody, setJsonBody] = useState(api.requestExample || '{}');
  const [jsonError, setJsonError] = useState('');
  const [formValues, setFormValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);

  const inputStyle = {
    flex: 1, padding: '7px 10px',
    border: '1px solid var(--border)', borderRadius: 6,
    background: 'var(--bg-base)', color: 'var(--text-primary)',
    fontSize: 12, fontFamily: 'JetBrains Mono, monospace', outline: 'none',
  };
  const useBtnStyle = {
    padding: '0 10px', borderRadius: 6, border: '1px solid var(--accent-glow)',
    background: 'var(--accent-bg)', color: 'var(--accent)',
    fontSize: 11, fontWeight: 600, cursor: 'pointer', flexShrink: 0,
  };

  useEffect(() => {
    if (savedBearerKey && !bearerKeyValue) setBearerKeyValue(savedBearerKey);
  }, [savedBearerKey]); // eslint-disable-line

  useEffect(() => {
    if (savedToken && !tokenValue) setTokenValue(savedToken);
  }, [savedToken]); // eslint-disable-line

  // Reset body when switching endpoints; inject saved token into example
  useEffect(() => {
    let example = api.requestExample || '{}';
    if (needsCorporateAuth && savedToken) {
      try {
        const parsed = JSON.parse(example);
        parsed.token = savedToken;
        example = JSON.stringify(parsed, null, 2);
      } catch { /* keep original */ }
    }
    setJsonBody(example);
    setFormValues({});
    setJsonError('');
    setResponse(null);
  }, [api.id]); // eslint-disable-line

  // Keep JSON body in sync with token field
  useEffect(() => {
    if (!needsCorporateAuth || !tokenValue || bodyMode !== 'json') return;
    setJsonBody(prev => {
      try {
        const parsed = JSON.parse(prev);
        if (parsed.token === tokenValue) return prev;
        parsed.token = tokenValue;
        return JSON.stringify(parsed, null, 2);
      } catch {
        return prev;
      }
    });
  }, [tokenValue, needsCorporateAuth, bodyMode]);

  const buildBodyFromForm = () => {
    const body = {};
    api.body.forEach(f => {
      const raw = formValues[f.field];
      if (raw === undefined || raw === '') return;
      if (f.type === 'object' || f.type === 'array') {
        try { body[f.field] = JSON.parse(raw); } catch { body[f.field] = raw; }
      } else if (f.type === 'number' || f.type === 'integer') {
        const n = parseFloat(raw); body[f.field] = isNaN(n) ? raw : n;
      } else body[f.field] = raw;
    });
    if (needsCorporateAuth && tokenValue) body.token = tokenValue;
    return body;
  };

  const getBody = () => {
    if (bodyMode === 'json') {
      try {
        const parsed = JSON.parse(jsonBody);
        setJsonError('');
        // Token field always wins over whatever is in the JSON editor
        if (needsCorporateAuth && tokenValue) {
          return { ...parsed, token: tokenValue };
        }
        return parsed;
      } catch {
        setJsonError('Invalid JSON');
        return null;
      }
    }
    return buildBodyFromForm();
  };

  const handleSend = async () => {
    if (!baseUrl) {
      setResponse({ status: 'ERR', time: 0, ok: false, body: { error: 'Set API environment in header first.' } });
      return;
    }
    if (hasBearerKey && !bearerKeyValue) {
      setResponse({ status: 'ERR', time: 0, ok: false, body: { error: 'bearerkey is required in header for this API.' } });
      return;
    }
    if (needsCorporateAuth && !tokenValue) {
      setResponse({ status: 'ERR', time: 0, ok: false, body: { error: 'token is required in body for this API.' } });
      return;
    }
    const body = getBody();
    if (body === null) return;

    setLoading(true);
    setResponse(null);
    const t0 = Date.now();
    try {
      const headers = buildHeaders({ api, bearerKey: bearerKeyValue, token: tokenValue });
      const res = await fetch(baseUrl + api.endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      const time = Date.now() - t0;
      let data;
      try { data = await res.json(); } catch { data = { raw: await res.text() }; }
      setResponse({ status: res.status, time, ok: res.ok, body: data });
      if (hasBearerKey && bearerKeyValue) setSavedBearerKey(bearerKeyValue);
      if (res.ok && data?.token) setSavedToken(data.token);
    } catch (err) {
      setResponse({ status: 'ERR', time: Date.now() - t0, ok: false, body: { error: err.message } });
    }
    setLoading(false);
  };

  const applyToken = (token) => {
    setTokenValue(token);
    if (bodyMode === 'json') {
      setJsonBody(prev => {
        try {
          const parsed = JSON.parse(prev);
          parsed.token = token;
          return JSON.stringify(parsed, null, 2);
        } catch {
          return prev;
        }
      });
    }
  };

  const fillExample = () => {
    let example = api.requestExample || '{}';
    const token = tokenValue || savedToken;
    if (needsCorporateAuth && token) {
      try {
        const parsed = JSON.parse(example);
        parsed.token = token;
        example = JSON.stringify(parsed, null, 2);
      } catch { /* keep original */ }
    }
    setJsonBody(example);
  };

  const rOk = response?.ok;
  const rColor = !response ? 'var(--text-muted)' : rOk ? 'var(--success)' : '#ef4444';

  const sectionLabel = {
    fontSize: 10, fontWeight: 700, color: 'var(--text-muted)',
    textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8,
  };

  const outerStyle = embedded ? {} : {
    position: 'sticky', top: 88,
    border: '1px solid var(--border)',
    borderRadius: 14,
    background: 'var(--bg-surface)',
    overflow: 'hidden',
    boxShadow: 'var(--shadow-md)',
  };

  const titleRow = (
    <div style={{
      height: 40,
      display: 'flex', alignItems: 'center', gap: 8,
      marginBottom: embedded ? 16 : 0,
      ...(embedded ? {} : {
        padding: '12px 14px',
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg-elevated)',
        height: 'auto',
      }),
    }}>
      <div style={{
        width: 26, height: 26, borderRadius: 7, flexShrink: 0,
        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="#fff"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.2 }}>Try API</div>
        {embedded && (
          <div style={{ fontSize: 10.5, color: 'var(--text-muted)', lineHeight: 1.2 }}>Live request</div>
        )}
      </div>
    </div>
  );

  return (
    <div style={outerStyle}>
      {titleRow}

      <div style={{ padding: embedded ? 0 : '14px' }}>

        {/* URL + Send */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <div style={{
            flex: '1 1 160px', display: 'flex', alignItems: 'center', gap: 6,
            padding: '0 10px', height: 36,
            border: '1px solid var(--border)', borderRadius: 8,
            background: 'var(--bg-base)', overflow: 'hidden', minWidth: 0,
          }}>
            <span style={{
              fontSize: 10, fontWeight: 800, color: 'var(--post)',
              fontFamily: 'JetBrains Mono, monospace', flexShrink: 0,
            }}>POST</span>
            <span style={{
              fontSize: 11, fontFamily: 'JetBrains Mono, monospace',
              color: 'var(--text-secondary)', overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {baseUrl + api.endpoint}
            </span>
          </div>
          <button
            onClick={handleSend}
            disabled={loading}
            style={{
              height: 36, padding: '0 16px', borderRadius: 8, border: 'none',
              background: loading ? 'var(--bg-hover)' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              color: loading ? 'var(--text-muted)' : '#fff',
              fontSize: 12, fontWeight: 700, cursor: loading ? 'wait' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              flex: '0 0 auto', minWidth: 80,
            }}
          >
            {loading ? <Spinner /> : 'Send'}
          </button>
        </div>

        {/* bearerkey */}
        {hasBearerKey && (
          <div style={{ marginBottom: 14 }}>
            <div style={sectionLabel}>bearerkey</div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                type="text"
                value={bearerKeyValue}
                onChange={e => setBearerKeyValue(e.target.value)}
                placeholder="XXXXXXXXXXXXXXXXXXXX"
                style={inputStyle}
              />
              {savedBearerKey && (
                <button onClick={() => setBearerKeyValue(savedBearerKey)} title="Use saved key" style={useBtnStyle}>
                  Use key
                </button>
              )}
            </div>
          </div>
        )}

        {/* Token */}
        {(needsCorporateAuth || hasAuthToken) && (
          <div style={{ marginBottom: 14 }}>
            <div style={sectionLabel}>{hasAuthToken ? 'Authorization' : 'token'}</div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                type="text"
                value={tokenValue}
                onChange={e => applyToken(e.target.value)}
                placeholder={product?.id === 'delivery' ? 'JWT from Authorize' : 'JWT from Corporate Authorize'}
                style={inputStyle}
              />
              {savedToken && (
                <button onClick={() => applyToken(savedToken)} title="Use saved token" style={useBtnStyle}>
                  Use token
                </button>
              )}
            </div>
          </div>
        )}

        {/* Body */}
        {api.body.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ ...sectionLabel, marginBottom: 0 }}>Body</div>
              <div style={{ display: 'flex', gap: 2, padding: 2, background: 'var(--bg-elevated)', borderRadius: 6, border: '1px solid var(--border)' }}>
                {['json', 'form'].map(m => (
                  <button key={m} onClick={() => setBodyMode(m)} style={{
                    padding: '3px 10px', borderRadius: 4, border: 'none', cursor: 'pointer',
                    fontSize: 10.5, fontWeight: 600, fontFamily: 'Inter, sans-serif',
                    background: bodyMode === m ? 'var(--bg-base)' : 'transparent',
                    color: bodyMode === m ? 'var(--text-primary)' : 'var(--text-muted)',
                    boxShadow: bodyMode === m ? 'var(--shadow-sm)' : 'none',
                  }}>{m === 'json' ? 'JSON' : 'Form'}</button>
                ))}
              </div>
            </div>

            {bodyMode === 'json' ? (
              <div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
                  <button onClick={fillExample} style={{
                    fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                    border: '1px solid var(--border)', background: 'var(--bg-elevated)',
                    color: 'var(--text-muted)', cursor: 'pointer',
                  }}>Fill example</button>
                </div>
                <textarea
                  value={jsonBody}
                  onChange={e => { setJsonBody(e.target.value); setJsonError(''); }}
                  rows={8}
                  style={{
                    width: '100%', boxSizing: 'border-box', padding: '10px 12px',
                    border: `1px solid ${jsonError ? '#ef4444' : 'var(--border)'}`,
                    borderRadius: 8, background: 'var(--code-bg)',
                    color: 'var(--text-primary)', fontSize: 12,
                    fontFamily: 'JetBrains Mono, monospace', lineHeight: 1.6,
                    resize: 'vertical', outline: 'none',
                  }}
                />
                {jsonError && <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>{jsonError}</div>}
              </div>
            ) : (
              <div style={{
                padding: '10px', border: '1px solid var(--border)',
                borderRadius: 8, background: 'var(--bg-base)',
              }}>
                {api.body.map((f, i) => (
                  <MiniField key={i} field={f} value={formValues[f.field]}
                    onChange={v => setFormValues(p => ({ ...p, [f.field]: v }))} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Response */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <div style={{ ...sectionLabel, marginBottom: 0 }}>Response</div>
            {response && (
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                background: rOk ? 'var(--success-bg)' : 'var(--error-bg)',
                color: rColor, fontFamily: 'JetBrains Mono, monospace',
                border: `1px solid ${rOk ? 'var(--success-border)' : 'var(--error-border)'}`,
              }}>
                {response.status} · {response.time}ms
              </span>
            )}
            {loading && <Spinner size={12} />}
          </div>

          {response?.ok && response.body?.token && (
            <div style={{
              fontSize: 11, color: 'var(--success)', padding: '6px 10px',
              background: 'var(--success-bg)', borderRadius: 6,
              border: '1px solid var(--success-border)', marginBottom: 8,
            }}>
              ✓ Token saved — auto-fills other endpoints
            </div>
          )}

          <div style={{
            borderRadius: 8, overflow: 'hidden',
            border: `1px solid ${response ? (rOk ? 'var(--success-border)' : 'var(--error-border)') : 'var(--border)'}`,
            background: 'var(--code-bg)', minHeight: 120,
          }}>
            {loading ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                <Spinner size={20} />
                <div style={{ marginTop: 8 }}>Sending...</div>
              </div>
            ) : response ? (
              <pre style={{
                margin: 0, padding: '12px 14px', overflowX: 'auto',
                fontSize: 11.5, lineHeight: 1.7, fontFamily: 'JetBrains Mono, monospace',
                maxHeight: 280, overflowY: 'auto',
              }}>
                {JSON.stringify(response.body, null, 2).split('\n').map((line, i) => (
                  <JsonLine key={i} line={line} />
                ))}
              </pre>
            ) : (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-disabled)', fontSize: 12 }}>
                Hit Send to see response
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
